import React, { useState, useRef, useLayoutEffect } from 'react';
import classNames from 'classnames';
import { SuggestionDataItem } from 'react-mentions';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import Button, { BUTTON_SIZES, BUTTON_TYPES } from '@buyside/components/presentation/Button/button';
import CommentBodyRenderer from '@buyside/components/presentation/CommentBodyRenderer/commentBodyRenderer';
import CommentAttachments from '@buyside/components/presentation/Comment/CommentAttachments/commentAttachments';
import CommentActions from '@buyside/components/presentation/Comment/CommentActions/commentActions';
import CommentHeader from '@buyside/components/presentation/Comment/CommentHeader/commentHeader';
import IdeaUpdateCommentForm from '@buyside/components/presentation/IdeaUpdates/IdeaUpdateCommentForm/ideaUpdateCommentForm';

import { useTruncatedComment } from '@buyside/util/hooks';

import {
  IdeaShowPageCommentFragment,
  Maybe,
  useLikeCommentMutation
} from '@interfaces/graphql-typings';

import styles from '@buyside/components/presentation/IdeaUpdates/IdeaUpdateComments/ideaUpdateCommentsStyles.scss';

const IdeaUpdateComment: React.FC<{
  allChildComments: Maybe<IdeaShowPageCommentFragment>[];
  authorId: string;
  authorName: Maybe<string>;
  canViewUserDetails: boolean;
  canWriteComments: boolean;
  comment: IdeaShowPageCommentFragment;
  isAdmin: boolean;
  isAuthor: boolean;
  ideaUpdateId: string;
  canCommentAnonymously: boolean;
  mentionables: SuggestionDataItem[];
  isAllocator: boolean;
  highlightedCommentId?: string;
  isIdeaAnonymous: boolean;
  isNonBuyside: boolean;
  currentUserId: string;
}> = ({
  allChildComments,
  authorId,
  authorName,
  canCommentAnonymously,
  canViewUserDetails,
  canWriteComments,
  comment,
  highlightedCommentId,
  ideaUpdateId,
  isAdmin,
  isAllocator,
  isAuthor,
  mentionables,
  isIdeaAnonymous,
  isNonBuyside,
  currentUserId
}): React.ReactElement => {
  const {
    anonUser,
    body,
    commentAttachments,
    createdAt,
    currentUserHasLiked,
    id: commentId,
    likeCount,
    responseRequested,
    user
  } = comment;

  const [showChildComments, setShowChildComments] = useState(true);
  const [showReply, setShowReply] = useState(false);

  const toggleReply = (): void => setShowReply(!showReply);
  const toggleShowChildComments = (): void => setShowChildComments(!showChildComments);

  const parentDivRef = useRef<HTMLDivElement>(null);
  const id = `update-comment-${commentId}-top`;

  const {
    handleToggleTruncation,
    truncatable,
    truncated,
    truncatedContentRef
  } = useTruncatedComment(id, comment?.body);

  const isAuthorComment = authorId === user?.id;
  const showUserInfo =
    canViewUserDetails || currentUserId === user?.id || (isNonBuyside && isAuthorComment);

  const childComments = allChildComments?.filter(c => c && c?.parentId === comment?.id);

  const [likeComment, { loading: likeLoading }] = useLikeCommentMutation({
    variables: { commentId },
    update(cache, { data }) {
      if (!data?.likeComment?.comment) return;
      const hasLiked = Boolean(data.likeComment.comment.currentUserHasLiked);

      cache.modify({
        id: cache.identify({ id: data.likeComment.comment.id, __typename: 'Comment' }),
        fields: { currentUserHasLiked: () => hasLiked }
      });
    }
  });

  useLayoutEffect(() => {
    if (Boolean(highlightedCommentId === comment?.id) && parentDivRef.current) {
      parentDivRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  });

  return (
    <div
      className={classNames(styles.commentContainer, {
        [styles.childComment]: Boolean(comment?.parentId)
      })}
    >
      <div className={styles.comment} id={id}>
        <CommentHeader
          anonUser={anonUser}
          authorName={authorName}
          createdAt={createdAt}
          isAdmin={isAdmin}
          isAuthor={isAuthor}
          isAuthorComment={isAuthorComment}
          isNonBuyside={isNonBuyside}
          responseRequested={Boolean(responseRequested)}
          showUserInfo={showUserInfo}
          user={user}
        />
        <div className={styles.commentBody}>
          <div
            className={classNames(styles.unselectable, { [styles.truncated]: truncated })}
            ref={truncatedContentRef}
          >
            <CommentBodyRenderer body={body} />
          </div>
          {truncatable ? (
            <Button
              onClick={handleToggleTruncation}
              size={BUTTON_SIZES.SMALL}
              text={truncated ? 'Read More' : 'Read Less'}
              type={BUTTON_TYPES.LINK}
            />
          ) : null}
        </div>
        <CommentAttachments commentAttachments={commentAttachments} />
        <CommentActions
          canWriteComments={canWriteComments}
          commentId={comment?.id}
          currentUserHasLiked={currentUserHasLiked}
          hasChildComments={Boolean(childComments?.length)}
          likeComment={likeComment}
          likeCount={likeCount}
          likeLoading={likeLoading}
          showChildComments={showChildComments}
          toggleReply={toggleReply}
          toggleShowChildComments={toggleShowChildComments}
        />
        {showReply && (
          <IdeaUpdateCommentForm
            canCommentAnonymously={canCommentAnonymously}
            ideaUpdateId={ideaUpdateId}
            isAllocator={isAllocator}
            isAuthor={isAuthor}
            isIdeaAnonymous={isIdeaAnonymous}
            isReplying
            mentionables={mentionables}
            parentId={comment.id}
          />
        )}
      </div>
      <div className={styles.childComments}>
        {showChildComments && (
          <TransitionGroup>
            {childComments?.map(childComment => {
              return childComment ? (
                <CSSTransition
                  key={`idea-update-comment-${childComment?.id}`}
                  classNames="fade"
                  timeout={500}
                  in
                  appear
                >
                  <IdeaUpdateComment
                    allChildComments={allChildComments}
                    authorId={authorId}
                    authorName={authorName}
                    canCommentAnonymously={canCommentAnonymously}
                    canViewUserDetails={canViewUserDetails}
                    canWriteComments={canWriteComments}
                    comment={childComment}
                    currentUserId={currentUserId}
                    highlightedCommentId={highlightedCommentId}
                    ideaUpdateId={ideaUpdateId}
                    isAdmin={isAdmin}
                    isAllocator={isAllocator}
                    isAuthor={isAuthor}
                    isIdeaAnonymous={isIdeaAnonymous}
                    isNonBuyside={isNonBuyside}
                    key={childComment?.id}
                    mentionables={mentionables}
                  />
                </CSSTransition>
              ) : null;
            })}
          </TransitionGroup>
        )}
      </div>
    </div>
  );
};

export default IdeaUpdateComment;
